import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import sellerApi from "../utils/sellerApi";
import {
  SELLER_AUTH_EVENT,
  clearSellerSession,
  getSellerToken,
  getSellerUser,
  migrateLegacyAuthStorage,
  setSellerSession,
  setSellerUser,
} from "../utils/authStorage";

const SellerAuthContext = createContext({
  seller: null,
  sellerLoading: false,
  isSellerAuthenticated: false,
  loginSeller: () => {},
  logoutSeller: () => {},
  refreshSellerSession: async () => false,
});

const buildSellerProfile = (storedSeller, sellerResponse) => {
  const responseSeller = sellerResponse?.data?.seller || {};

  return {
    ...(storedSeller || {}),
    ...responseSeller,
    role: "seller",
    role_id: 2,
  };
};

export function SellerAuthProvider({ children }) {
  const [seller, setSeller] = useState(() => getSellerUser());
  const [sellerLoading, setSellerLoading] = useState(() => Boolean(getSellerToken()));
  const [isSellerAuthenticated, setIsSellerAuthenticated] = useState(false);

  const logoutSeller = useCallback(() => {
    clearSellerSession();
    setSeller(null);
    setSellerLoading(false);
    setIsSellerAuthenticated(false);
  }, []);

  const refreshSellerSession = useCallback(async () => {
    const sellerToken = getSellerToken();

    if (!sellerToken) {
      setSeller(null);
      setSellerLoading(false);
      setIsSellerAuthenticated(false);
      return false;
    }

    try {
      setSellerLoading(true);

      const response = await sellerApi.get("getSellerDashboard");
      const nextSeller = buildSellerProfile(getSellerUser(), response.data);

      setSellerUser(nextSeller, { notify: false });
      setSeller(nextSeller);
      setIsSellerAuthenticated(true);

      return true;
    } catch {
      clearSellerSession();
      setSeller(null);
      setIsSellerAuthenticated(false);
      return false;
    } finally {
      setSellerLoading(false);
    }
  }, []);

  const loginSeller = useCallback((payload = {}) => {
    const nextSeller = {
      ...(payload?.user || {}),
      role: "seller",
      role_id: 2,
    };

    setSeller(nextSeller);
    setSellerLoading(false);
    setIsSellerAuthenticated(Boolean(payload?.token));
    setSellerSession(payload);
  }, []);

  useEffect(() => {
    migrateLegacyAuthStorage();

    if (!getSellerToken()) {
      setSeller(getSellerUser());
      setSellerLoading(false);
      setIsSellerAuthenticated(false);
      return;
    }

    refreshSellerSession();
  }, [refreshSellerSession]);

  useEffect(() => {
    const handleSellerAuthChange = () => {
      const sellerToken = getSellerToken();

      if (!sellerToken) {
        setSeller(null);
        setSellerLoading(false);
        setIsSellerAuthenticated(false);
        return;
      }

      if (!isSellerAuthenticated) {
        refreshSellerSession();
        return;
      }

      setSeller(getSellerUser());
    };

    window.addEventListener(SELLER_AUTH_EVENT, handleSellerAuthChange);
    window.addEventListener("storage", handleSellerAuthChange);

    return () => {
      window.removeEventListener(SELLER_AUTH_EVENT, handleSellerAuthChange);
      window.removeEventListener("storage", handleSellerAuthChange);
    };
  }, [isSellerAuthenticated, refreshSellerSession]);

  const value = useMemo(
    () => ({
      seller,
      sellerLoading,
      isSellerAuthenticated,
      loginSeller,
      logoutSeller,
      refreshSellerSession,
    }),
    [
      seller,
      sellerLoading,
      isSellerAuthenticated,
      loginSeller,
      logoutSeller,
      refreshSellerSession,
    ],
  );

  return (
    <SellerAuthContext.Provider value={value}>
      {children}
    </SellerAuthContext.Provider>
  );
}

export const useSellerAuth = () => useContext(SellerAuthContext);
